/**
 * Firebase Configuration - Options Oracle V2
 * Centralized Firebase initialization with Auth & Firestore
 */
import { type FirebaseApp, initializeApp } from 'firebase/app'
import { type Auth, browserLocalPersistence, GoogleAuthProvider, getAuth, setPersistence, onIdTokenChanged } from 'firebase/auth'
import { type Firestore, initializeFirestore } from 'firebase/firestore'

// Firebase configuration from environment or hardcoded fallback
const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'AIzaSyCLHd4v1qVKDpc1cuea0hmS0nHQtd3a238',
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'studio-4081197442-105d3.firebaseapp.com',
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'studio-4081197442-105d3',
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'studio-4081197442-105d3.firebasestorage.app',
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '1039446706859',
    appId: import.meta.env.VITE_FIREBASE_APP_ID || '1:1039446706859:web:0b4c543827e869958c05ee',
}

// Singleton instances
let app: FirebaseApp | null = null
let auth: Auth | null = null
let db: Firestore | null = null
let googleProvider: GoogleAuthProvider | null = null

/**
 * Initialize Firebase App (Singleton Pattern)
 * Safe to call multiple times - returns existing instance
 */
export function getFirebaseApp(): FirebaseApp {
    if (!app) {
        app = initializeApp(firebaseConfig)
        // console.log("[Firebase] App initialized");
    }
    return app
}

/**
 * Get Firebase Auth instance
 */
export function getFirebaseAuth(): Auth {
    if (!auth) {
        const firebaseApp = getFirebaseApp()
        auth = getAuth(firebaseApp)

        // Set persistence to local (survives browser restart)
        if (typeof window !== 'undefined') {
            setPersistence(auth, browserLocalPersistence).catch((error) => {
                console.error('[Firebase] Persistence Error:', error)
            })

            // Native zero-overhead token rotation listener
            onIdTokenChanged(auth, async (user) => {
                if (user) {
                    try {
                        const token = await user.getIdToken(false)
                        setCachedIdToken(token)
                        if (typeof window !== 'undefined') {
                            window.dispatchEvent(new CustomEvent('auth_token_changed', { detail: { token } }))
                        }
                    } catch {}
                } else {
                    setCachedIdToken(null)
                    if (typeof window !== 'undefined') {
                        window.dispatchEvent(new CustomEvent('auth_token_changed', { detail: { token: null } }))
                    }
                }
            })
        }
    }
    return auth
}

/**
 * Get Firestore instance with optimized settings
 */
export function getFirestore(): Firestore {
    if (!db) {
        const firebaseApp = getFirebaseApp()
        db = initializeFirestore(firebaseApp, {
            experimentalForceLongPolling: true, // Avoid QUIC errors
        })
        // console.log("[Firebase] Firestore initialized");
    }
    return db
}

/**
 * Get Google Auth Provider
 */
export function getGoogleProvider(): GoogleAuthProvider {
    if (!googleProvider) {
        googleProvider = new GoogleAuthProvider()
        googleProvider.setCustomParameters({
            prompt: 'select_account',
        })
    }
    return googleProvider
}

// Export convenience getters
export { auth, db, googleProvider }

/**
 * Cached ID token for instant synchronous access by services & workers
 */
let cachedIdToken: string | null = null

export function setCachedIdToken(token: string | null) {
    cachedIdToken = token
    if (typeof window !== 'undefined') {
        if (token) localStorage.setItem('auth_id_token', token)
        else localStorage.removeItem('auth_id_token')
    }
}

export function getCachedIdToken(): string | null {
    if (cachedIdToken) return cachedIdToken
    if (typeof window !== 'undefined') {
        return localStorage.getItem('auth_id_token')
    }
    return null
}

/**
 * Get or wait for a valid Firebase ID Token
 * Ensures background services never fire unauthenticated requests on initial mount.
 */
export async function getOrWaitForAuthToken(): Promise<string | null> {
    if (typeof window === 'undefined') return null

    // 1. Try currently cached token
    const cached = getCachedIdToken()
    if (cached) {
        // Also refresh in background if currentUser is available
        const authInstance = getFirebaseAuth()
        if (authInstance.currentUser) {
            authInstance.currentUser.getIdToken(false).then((fresh) => {
                setCachedIdToken(fresh)
            }).catch(() => {})
        }
        return cached
    }

    // 2. Try currentUser directly
    const authInstance = getFirebaseAuth()
    if (authInstance.currentUser) {
        try {
            const token = await authInstance.currentUser.getIdToken(false)
            setCachedIdToken(token)
            return token
        } catch {}
    }

    // 3. If Firebase is still initializing, wait for auth state (up to 3 seconds)
    return new Promise((resolve) => {
        const timeout = setTimeout(() => {
            unsubscribe()
            resolve(getCachedIdToken())
        }, 3000)

        const unsubscribe = authInstance.onAuthStateChanged(async (user) => {
            if (user) {
                clearTimeout(timeout)
                unsubscribe()
                try {
                    const token = await user.getIdToken(false)
                    setCachedIdToken(token)
                    resolve(token)
                } catch {
                    resolve(null)
                }
            }
        })
    })
}

